import React, { useState } from 'react';
import MDBox from 'src/components/md/MDBox';
import MDTypography from 'src/components/md/MDTypography';
import { CaretDownIcon, CaretUpIcon } from '@phosphor-icons/react';
import { Plural, T, Var, useGT } from 'gt-react';
import { formatEdiInterchangeDateTime } from '@/utils/ediInterchangeDateTime';
import { EdiNotesCard } from './EdiNotes';
import type { EdiNote } from 'src/types/application/SalesOrderDraftDTO';

/** Links the raw-payload toggle to the region it controls, for screen readers. */
const RAW_DATA_PANEL_ID = 'edi-raw-data-panel';

interface Props {
  ordefulMessage: Record<string, unknown>;
  /**
   * Notes lifted from the inbound 850, rendered under Party Information —
   * the partner's instructions belong beside the parties that sent them.
   * Only the primary transaction passes these; the 860s in the history
   * timeline leave it undefined so the notes render exactly once.
   */
  ediNotes?: EdiNote[] | null;
  /**
   * Rendered between the notes and the raw-payload accordion — the order
   * history goes here so the technical dump stays last. Only the top-level
   * viewer passes it; the per-entry viewers inside the history leave it
   * undefined, which is what keeps this from nesting into itself.
   */
  beforeRawData?: React.ReactNode;
}

interface OrdefulMessage {
  payload: {
    sender: {
      isaId: string;
      name: string;
    };
    receiver: {
      isaId: string;
      name: string;
    };
    id: string;
    controlNumber: string;
    type: {
      name: string;
    };
    businessNumber: string;
    message: {
      interchangeControlHeader: [
        {
          interchangeDate: string;
          interchangeTime: string;
        },
      ];
      transactionSets: [
        {
          N1_loop: [
            {
              partyLocation: [
                {
                  addressInformation: string;
                },
              ];
              geographicLocation: [
                {
                  cityName: string;
                  postalCode: string;
                  countryCode: string;
                  stateOrProvinceCode: string;
                },
              ];
              partyIdentification: [
                {
                  name: string;
                  identificationCode: string;
                  entityIdentifierCode: string;
                  identificationCodeQualifier: string;
                },
              ];
            },
          ];
        },
      ];
    };
  };
}

// Custom JSON viewer to match the pattern used elsewhere in the codebase
interface JsonObject {
  [key: string]: JsonValue;
}
type JsonArray = JsonValue[];
type JsonValue =
  string | number | boolean | null | undefined | JsonObject | JsonArray;

const JsonViewer = ({
  data,
  level = 0,
}: {
  data: JsonValue;
  level?: number;
}) => {
  const gt = useGT();
  const [expanded, setExpanded] = useState<Record<string, boolean>>({
    // Expand first level by default
    root: level < 1,
  });

  if (data === null)
    return (
      <span style={{ color: '#999' }}>
        <T context="json viewer null value">null</T>
      </span>
    );
  if (data === undefined)
    return (
      <span style={{ color: '#999' }}>
        <T context="json viewer undefined value">undefined</T>
      </span>
    );

  if (typeof data !== 'object') {
    // Handle primitive values
    if (typeof data === 'string') {
      return (
        <span
          style={{
            color: '#009688',
            wordBreak: 'break-word',
            whiteSpace: 'pre-wrap',
          }}
        >
          <T context="json string value with quotes">
            &quot;<Var>{data}</Var>&quot;
          </T>
        </span>
      );
    }
    if (typeof data === 'number') {
      return <span style={{ color: '#1976d2' }}>{data}</span>;
    }
    if (typeof data === 'boolean') {
      return (
        <span style={{ color: '#ff9800' }}>
          {data ? (
            <T context="json boolean true">true</T>
          ) : (
            <T context="json boolean false">false</T>
          )}
        </span>
      );
    }
    return <span style={{ wordBreak: 'break-word' }}>{String(data)}</span>;
  }

  if (Array.isArray(data)) {
    if (data.length === 0) {
      return (
        <span>
          <T context="json empty array display">[ ]</T>
        </span>
      );
    }

    return (
      <div style={{ paddingLeft: level > 0 ? 20 : 0, width: '100%' }}>
        {data.length > 1 && (
          <span
            onClick={() =>
              setExpanded((prev) => ({ ...prev, root: !prev.root }))
            }
            style={{ cursor: 'pointer', userSelect: 'none' }}
          >
            {expanded.root
              ? gt('▼', { context: 'json viewer expanded node' })
              : gt('▶', { context: 'json viewer collapsed node' })}{' '}
            <T context="json array viewer label with element count">
              Array [<Var>{data.length}</Var>]
            </T>
          </span>
        )}

        {data.length === 1 && (
          <div style={{ paddingLeft: 20, width: '100%' }}>
            {data.map((item, i) => (
              <div key={i} style={{ width: '100%' }}>
                <JsonViewer data={item} level={level + 1} />
              </div>
            ))}
          </div>
        )}

        {expanded.root && data.length > 1 && (
          <div style={{ paddingLeft: 20, width: '100%' }}>
            {data.map((item, i) => (
              <div key={i} style={{ width: '100%' }}>
                <span style={{ color: '#555', marginRight: 8 }}>
                  <T context="json array element index prefix">
                    [<Var>{i}</Var>]:
                  </T>
                </span>
                <JsonViewer data={item} level={level + 1} />
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Object
  const keys = Object.keys(data);
  if (keys.length === 0) {
    return (
      <span>
        <T context="json empty object display">{'{}'}</T>
      </span>
    );
  }

  return (
    <div style={{ paddingLeft: level > 0 ? 20 : 0, width: '100%' }}>
      {keys.length > 1 && (
        <span
          onClick={() => setExpanded((prev) => ({ ...prev, root: !prev.root }))}
          style={{ cursor: 'pointer', userSelect: 'none' }}
        >
          {expanded.root
            ? gt('▼', { context: 'json viewer expanded node' })
            : gt('▶', { context: 'json viewer collapsed node' })}{' '}
          <T context="json object viewer label">Object</T>{' '}
          <T context="json object key count in braces">
            {'{'}
            <Var>{keys.length}</Var>{' '}
            <Plural n={keys.length} one="key" other="keys" />
            {'}'}
          </T>
        </span>
      )}

      {(expanded.root || keys.length === 1) && (
        <div style={{ paddingLeft: 20, width: '100%' }}>
          {keys.map((key) => (
            <div
              key={key}
              style={{
                wordBreak: 'break-word',
                marginBottom: 2,
                width: '100%',
              }}
            >
              <span
                style={{ color: '#673ab7', fontWeight: 'bold', marginRight: 8 }}
              >
                <Var>{key}</Var>:
              </span>
              <JsonViewer data={data[key]} level={level + 1} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * The raw payload, behind a collapsed toggle.
 *
 * Extracted so its open/closed branching stops counting against the viewer's
 * complexity budget, which is already at its configured ceiling. Self-contained
 * by nature: nothing else on the panel reacts to this being open.
 */
function RawEdiDataAccordion({ payload }: { payload: JsonValue }) {
  const [rawDataVisible, setRawDataVisible] = useState(false);

  return (
    <MDBox
      sx={{
        // Only claim the remaining vertical space once opened — growing while
        // closed left a tall blank gap under the toggle.
        flex: rawDataVisible ? 1 : '0 0 auto',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <MDBox
        component="button"
        type="button"
        aria-expanded={rawDataVisible}
        aria-controls={RAW_DATA_PANEL_ID}
        onClick={() => setRawDataVisible((prev) => !prev)}
        sx={{
          // A div with onClick is not keyboard-operable, which matters now that
          // the payload is collapsed by default.
          appearance: 'none',
          background: 'none',
          border: 0,
          font: 'inherit',
          color: 'inherit',
          textAlign: 'left',
          width: '100%',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          p: 0.5,
          borderRadius: '4px',
          flexShrink: 0,
          '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' },
        }}
      >
        {rawDataVisible ? (
          <CaretUpIcon size={16} aria-hidden />
        ) : (
          <CaretDownIcon size={16} aria-hidden />
        )}
        <MDTypography variant="caption" fontWeight="bold">
          <T context="collapsible section header for the raw EDI payload">
            Raw EDI Data
          </T>
        </MDTypography>
      </MDBox>

      <MDBox
        id={RAW_DATA_PANEL_ID}
        sx={{
          display: rawDataVisible ? 'block' : 'none',
          border: '1px solid #e0e0e0',
          borderRadius: '8px',
          p: 1,
          backgroundColor: '#fafafa',
          flex: 1,
          overflow: 'auto',
          minHeight: 0,
          fontFamily: 'var(--font-mono)',
          fontSize: '12px',
          lineHeight: 1.4,
        }}
      >
        <JsonViewer data={payload} />
      </MDBox>
    </MDBox>
  );
}

const EdiTransactionViewer: React.FC<Props> = ({
  ordefulMessage,
  ediNotes,
  beforeRawData,
}) => {
  const gt = useGT();
  const msg = (ordefulMessage as unknown as OrdefulMessage)?.payload;
  const na = gt('N/A', {
    context: 'placeholder when EDI field is missing',
  });
  const unknownName = gt('??', {
    context: 'placeholder when trading partner name is unknown in EDI viewer',
  });

  return (
    <MDBox
      sx={{
        width: '100%',
        overflowX: 'hidden', // Prevent horizontal overflow
        p: 2,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <MDTypography variant="h6" mb={1}>
        <T context="EDI transaction detail panel title">
          EDI Transaction Details
        </T>
      </MDTypography>

      {/* Trading Partner Information */}
      <MDBox mb={1} sx={{ flexShrink: 0 }}>
        <MDBox
          sx={{
            backgroundColor: '#f8f9fa',
            borderRadius: '8px',
            p: 1,
            pl: 2,
            mb: 1,
          }}
        >
          <MDBox
            sx={{
              display: 'grid',
              gridTemplateColumns: '140px 1fr',
              gap: 1,
              mb: 1,
            }}
          >
            <MDTypography variant="caption" fontWeight="bold">
              <T context="EDI interchange sender label">Sender:</T>
            </MDTypography>
            <MDTypography variant="caption">
              {msg?.sender?.isaId || na}{' '}
              <T context="EDI sender name in parentheses">
                (<Var>{msg?.sender?.name || unknownName}</Var>)
              </T>
            </MDTypography>
          </MDBox>
          <MDBox
            sx={{
              display: 'grid',
              gridTemplateColumns: '140px 1fr',
              gap: 1,
              mb: 1,
            }}
          >
            <MDTypography variant="caption" fontWeight="bold">
              <T context="EDI interchange receiver label">Receiver:</T>
            </MDTypography>
            <MDTypography variant="caption">
              {msg?.receiver?.isaId || na}{' '}
              <T context="EDI receiver name in parentheses">
                (<Var>{msg?.receiver?.name || unknownName}</Var>)
              </T>
            </MDTypography>
          </MDBox>
          <MDBox
            sx={{
              display: 'grid',
              gridTemplateColumns: '140px 1fr',
              gap: 1,
              mb: 1,
            }}
          >
            <MDTypography variant="caption" fontWeight="bold">
              <T context="EDI transaction identifier label">Transaction ID:</T>
            </MDTypography>
            <MDTypography variant="caption">
              {msg?.id || na}
              {/* Inline rather than a standalone button: this panel shares the
                  modal with the line items, so a whole row for one link is
                  vertical space the order itself needs. */}
              {msg?.id && (
                <>
                  {' · '}
                  <MDTypography
                    component="a"
                    variant="caption"
                    color="info"
                    href={`https://ui.orderful.com/transactions/${msg.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      textDecoration: 'none',
                      '&:hover': { textDecoration: 'underline' },
                    }}
                  >
                    <T context="link label to view the EDI transaction in Orderful">
                      View in Orderful
                    </T>
                  </MDTypography>
                </>
              )}
            </MDTypography>
          </MDBox>
          <MDBox
            sx={{
              display: 'grid',
              gridTemplateColumns: '140px 1fr',
              gap: 1,
              mb: 1,
            }}
          >
            <MDTypography variant="caption" fontWeight="bold">
              <T context="EDI transaction type label">Transaction Type:</T>
            </MDTypography>
            <MDTypography variant="caption">
              {msg?.type?.name || na}
            </MDTypography>
          </MDBox>
          <MDBox
            sx={{
              display: 'grid',
              gridTemplateColumns: '140px 1fr',
              gap: 1,
              mb: 1,
            }}
          >
            <MDTypography variant="caption" fontWeight="bold">
              <T context="EDI business number label">Business Number:</T>
            </MDTypography>
            <MDTypography variant="caption">
              {msg?.businessNumber || na}
            </MDTypography>
          </MDBox>
          <MDBox
            sx={{
              display: 'grid',
              gridTemplateColumns: '140px 1fr',
              gap: 1,
            }}
          >
            <MDTypography variant="caption" fontWeight="bold">
              <T context="EDI interchange date and time label">Date/Time:</T>
            </MDTypography>
            <MDTypography variant="caption">
              {formatEdiInterchangeDateTime(
                msg?.message?.interchangeControlHeader?.[0]?.interchangeDate,
                msg?.message?.interchangeControlHeader?.[0]?.interchangeTime,
              ) ?? na}
            </MDTypography>
          </MDBox>
        </MDBox>
        <MDTypography variant="subtitle2" fontWeight="bold" mb={1}>
          <T context="EDI party N1 loop section title">Party Information</T>
        </MDTypography>
        <MDBox
          sx={{
            backgroundColor: '#f8f9fa',
            borderRadius: '8px',
            p: 2,
            mb: 2,
          }}
        >
          {(
            ordefulMessage as unknown as OrdefulMessage
          )?.payload?.message?.transactionSets?.[0]?.N1_loop?.map(
            (n1Loop, index) => (
              <MDBox
                key={index}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '20px 1fr',
                  gap: 1,
                  mb: 1,
                }}
              >
                <MDTypography variant="caption" fontWeight="bold">
                  {n1Loop?.partyIdentification?.[0]?.entityIdentifierCode || na}
                </MDTypography>
                <MDTypography variant="caption">
                  {n1Loop?.partyIdentification?.[0]?.name || na}{' '}
                  <T context="EDI party identification code in parentheses">
                    (#
                    <Var>
                      {n1Loop?.partyIdentification?.[0]?.identificationCode ||
                        na}
                    </Var>
                    )
                  </T>{' '}
                  {n1Loop?.partyLocation?.[0]?.addressInformation || na}
                  {', '}
                  {n1Loop?.geographicLocation?.[0]?.cityName || na}
                  {', '}
                  {n1Loop?.geographicLocation?.[0]?.stateOrProvinceCode ||
                    na}{' '}
                  {n1Loop?.geographicLocation?.[0]?.postalCode || na}
                </MDTypography>
              </MDBox>
            ),
          )}
        </MDBox>
      </MDBox>

      {/* Partner instructions from the 850's N9/MSG loops. Sits directly under
          Party Information: the notes are addressed to us by the same parties
          listed above, so they read together. Self-hides when there are none. */}
      {ediNotes && ediNotes.length > 0 && (
        <MDBox sx={{ flexShrink: 0 }}>
          <EdiNotesCard ediNotes={ediNotes} />
        </MDBox>
      )}

      {beforeRawData && (
        <MDBox sx={{ flexShrink: 0, mb: 1 }}>{beforeRawData}</MDBox>
      )}

      <RawEdiDataAccordion payload={ordefulMessage.payload as JsonValue} />
    </MDBox>
  );
};

export default EdiTransactionViewer;
